/**
 * Hook for managing team members
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TeamMember, TeamMemberFormData } from '@/types/evaluation';
import { useAuth } from '@/components/AuthProvider';

export const useTeamMembers = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch team members
  const fetchTeamMembers = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('team_members')
        .select('*')
        .eq('manager_user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setTeamMembers(data || []);
    } catch (err: any) {
      console.error('Error fetching team members:', err);
      setError(err.message || 'Không thể tải danh sách nhân viên');
    } finally {
      setIsLoading(false);
    }
  };

  // Create team member
  const createTeamMember = async (formData: TeamMemberFormData): Promise<TeamMember | null> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error: insertError } = await supabase
        .from('team_members')
        .insert([
          {
            manager_user_id: user.id,
            ...formData,
          },
        ])
        .select()
        .single();

      if (insertError) throw insertError;

      // Refresh list
      await fetchTeamMembers();

      return data;
    } catch (err: any) {
      console.error('Error creating team member:', err);
      throw new Error(err.message || 'Không thể tạo nhân viên');
    }
  };

  // Update team member
  const updateTeamMember = async (id: string, formData: Partial<TeamMemberFormData>): Promise<TeamMember | null> => {
    try {
      const { data, error: updateError } = await supabase
        .from('team_members')
        .update(formData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Refresh list
      await fetchTeamMembers();

      return data;
    } catch (err: any) {
      console.error('Error updating team member:', err);
      throw new Error(err.message || 'Không thể cập nhật nhân viên');
    }
  };

  // Delete team member
  const deleteTeamMember = async (id: string): Promise<void> => {
    try {
      const { error: deleteError } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Refresh list
      await fetchTeamMembers();
    } catch (err: any) {
      console.error('Error deleting team member:', err);
      throw new Error(err.message || 'Không thể xóa nhân viên');
    }
  };

  // Get active team members
  const getActiveTeamMembers = (): TeamMember[] => {
    return teamMembers.filter(member => member.is_active);
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [user]);

  return {
    teamMembers,
    activeTeamMembers: getActiveTeamMembers(),
    isLoading,
    error,
    fetchTeamMembers,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
  };
};

